import React, { useEffect, useState } from "react";
import { Box, Heading, Flex, Select, Input, IconButton, Text, VStack, Badge, Container, useColorModeValue, useTheme } from "@chakra-ui/react";
import { EditIcon } from "@chakra-ui/icons";
import axios from "axios";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import TableWeekly from "./components/TableWeekly";
import TableMonthly from "./components/TableMonthly";
import TableReason from "./components/TableReason";
import TableComment from "./components/TableComment";
import EditStrikeRate from "./components/EditStrikeRate";
import Exports from "./components/Exports";
import { useAuthentication } from "../../wrappers/UseAuthentication";

const DashboardCard = ({ title, children, flex, ...rest }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const headingBg = useColorModeValue("primary1", "darkPrimary1");
  const theme = useTheme();

  return (
    <Box
      flex={flex}
      bg={cardBg}
      borderRadius="lg"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      position="relative"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "md" }}
      _before={{
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        bg: `linear-gradient(to right, ${theme.colors.primary1}, ${theme.colors.primary2})`,
        borderTopLeftRadius: "lg",
        borderTopRightRadius: "lg",
      }}
      {...rest}
    >
      {title && (
        <Box bg={headingBg} px={3} py={1.5}>
          <Text fontSize="sm" fontWeight="bold" color="white">{title}</Text>
        </Box>
      )}
      <Box p={2}>{children}</Box>
    </Box>
  );
};

const StrikeRate = () => {
  const [ddStrikeRate, setDdStrikeRate] = useState([]);
  const [weekEndingDates, setWeekEndingDates] = useState([]);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { user_type } = useAuthentication();
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const [loading, setLoading] = useState(false);

  const [weekEndingDate, setWeekEndingDate] = useState("");
  const [comment, setComment] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchCommitted, setSearchCommitted] = useState("");

  const theme = useTheme();

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [selectedRetailer, selectedSupplier]);

  useEffect(() => {
    if (weekEndingDates.length > 0 && !weekEndingDate) {
      setWeekEndingDate(weekEndingDates[0].week_ending_date);
    }
  }, [weekEndingDates]);

  const loadData = async () => {
    await fetchDdStrikeRate();
    await fetchWeekEndingDates();
    setLoading(false);
  };

  const fetchDdStrikeRate = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/get-dd-strike-rate`, {});
      setDdStrikeRate(res.data.data);
    } catch (error) {
      console.error("Error fetching strike rate data:", error);
    }
  };

  const fetchWeekEndingDates = async () => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/rep_strikeRate/week-ending-date`, {
        retailer: selectedRetailer,
      });
      setWeekEndingDates(res.data.data);
    } catch (error) {
      console.error("Error fetching week ending dates:", error);
    }
  };

  const handleToggleEdit = () => {
    setIsEditOpen(!isEditOpen);
  };

  function formatDateString(dateString) {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const date = new Date(dateString);
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${day} ${month} '${year}`;
  }

  return (
    <Box minH="100vh" bg="gray.50" p={4}>
      <Container maxW="98vw" mx="auto">
        <VStack spacing={4} align="stretch">
          {/* Header Card */}
          <Box bg="white" borderRadius="lg" shadow="md" border="1px" borderColor="gray.200" p={2}>
            <Flex align="center" justify="space-between" gap={3}>
              {/* Left: Heading + Badges */}
              <VStack align="start" spacing={1} flexShrink={0}>
                <Heading size="md" color={theme.colors.primary1} fontWeight="bold">
                  Strike Rate
                </Heading>
                <Flex gap={2}>
                  <Badge bg={theme.colors.primary1} color="white" px={1} py={0.5} borderRadius="md" fontSize="2xs">
                    Supplier: {selectedSupplier}
                  </Badge>
                  <Badge bg={theme.colors.secondary1} color="white" px={1} py={0.5} borderRadius="md" fontSize="2xs">
                    Retailer: {selectedRetailer}
                  </Badge>
                </Flex>
              </VStack>

              {/* Center: Date + Reason dropdowns + Search */}
              <Flex align="center" gap={2} flexWrap="wrap" justify="center" flex={1}>
                <Select
                  size="sm"
                  maxW="200px"
                  bg="white"
                  borderColor="gray.300"
                  value={weekEndingDate}
                  onChange={(e) => setWeekEndingDate(e.target.value)}
                >
                  {weekEndingDates.map((date, index) => (
                    <option key={index} value={date.week_ending_date}>
                      {formatDateString(date.week_ending_date)}
                    </option>
                  ))}
                </Select>
                <Select
                  size="sm"
                  maxW="200px"
                  bg="white"
                  borderColor="gray.300"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                >
                  <option value="">All Reasons</option>
                  {ddStrikeRate.map((item) => (
                    <option key={item.uuid} value={item.uuid}>
                      {item.text}
                    </option>
                  ))}
                </Select>
                <Input
                  size="sm"
                  maxW="180px"
                  placeholder="Search..."
                  bg="white"
                  borderColor="gray.300"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      setSearchCommitted(searchInput);
                    }
                  }}
                />
              </Flex>

              {/* Right: Export */}
              <Box position="relative" zIndex={2000} flexShrink={0}>
                <Exports supplier={selectedSupplier} retailer={selectedRetailer} week_ending_date={weekEndingDate} dd_uuid={comment} />
              </Box>
            </Flex>
          </Box>

          {/* Summary Tables Row */}
          <Flex gap={4}>
            <DashboardCard title="Weekly" flex="1">
              <TableWeekly selectedRetailer={selectedRetailer} selectedSupplier={selectedSupplier} />
            </DashboardCard>
            <DashboardCard title="Monthly" flex="1">
              <TableMonthly selectedRetailer={selectedRetailer} selectedSupplier={selectedSupplier} />
            </DashboardCard>
            <DashboardCard title="Reason" flex="1.5">
              <TableReason selectedRetailer={selectedRetailer} selectedSupplier={selectedSupplier} ddStrikeRate={ddStrikeRate} />
            </DashboardCard>
          </Flex>

          {/* Comment Detail Table */}
          <DashboardCard title="Comment Detail">
            {!loading && weekEndingDates.length > 0 && weekEndingDate ? (
              <TableComment
                selectedRetailer={selectedRetailer}
                selectedSupplier={selectedSupplier}
                weekEndingDates={weekEndingDates}
                ddStrikeRate={ddStrikeRate}
                weekEndingDate={weekEndingDate}
                comment={comment}
                search={searchCommitted}
              />
            ) : (
              <Box>Loading...</Box>
            )}
          </DashboardCard>
        </VStack>
      </Container>

      {/* Edit Popover */}
      {isEditOpen && (
        <EditStrikeRate
          isOpen={isEditOpen}
          onClose={handleToggleEdit}
          selectedRetailer={selectedRetailer}
          selectedSupplier={selectedSupplier}
          onDataSaved={loadData}
        />
      )}

      {/* Admin Edit Button */}
      {user_type === 'admin' && (
        <IconButton
          icon={<EditIcon />}
          aria-label="Edit Strike Rate"
          colorScheme="blue"
          position="fixed"
          bottom="30px"
          right="30px"
          size="lg"
          isRound
          boxShadow="lg"
          onClick={handleToggleEdit}
          zIndex={999}
        />
      )}
    </Box>
  );
};

export default StrikeRate;
